import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { contentAPI, GeneratedContent, Category } from '../services/contentAPI';

interface FilterState {
  search: string;
  category: string;
  status: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

const BlogManage: React.FC = () => {
  const [contents, setContents] = useState<GeneratedContent[]>([]);
  const [filteredContents, setFilteredContents] = useState<GeneratedContent[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    category: '',
    status: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  const [deleting, setDeleting] = useState<number | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [contents, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 카테고리 먼저 로드
      const categoriesData = await contentAPI.getCategories();
      setCategories(categoriesData);
      
      // 모든 콘텐츠 로드 (관리용)
      try {
        const contentsData = await contentAPI.getAllContents();
        setContents(contentsData);
      } catch (err) {
        // getAllContents가 실패하면 빈 배열로 초기화
        console.warn('getAllContents failed, trying alternative approach:', err);
        setContents([]);
      }
    } catch (err) {
      console.error('데이터 로딩 실패:', err);
      setError('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...contents];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(content =>
        content.title.toLowerCase().includes(searchLower) ||
        (content.keywords && content.keywords.some(keyword => keyword.toLowerCase().includes(searchLower)))
      );
    }

    if (filters.category) {
      filtered = filtered.filter(content =>
        content.category && content.category.name === filters.category
      );
    }

    if (filters.status) {
      filtered = filtered.filter(content => content.status === filters.status);
    }

    // 정렬 적용
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (filters.sortBy) {
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'updated_at':
          aValue = new Date(a.updated_at || a.created_at).getTime();
          bValue = new Date(b.updated_at || b.created_at).getTime();
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'seo_score':
          aValue = a.seo_score || 0;
          bValue = b.seo_score || 0;
          break;
        default:
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
      }

      if (filters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    setFilteredContents(filtered);
  };

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDelete = async (contentId: number) => {
    if (!window.confirm('정말 이 글을 삭제하시겠습니까?')) {
      return;
    }

    try {
      setDeleting(contentId);
      await contentAPI.deleteContent(contentId);
      setContents(contents.filter(content => content.id !== contentId));
    } catch (err) {
      setError(err instanceof Error ? err.message : '글 삭제에 실패했습니다.');
    } finally {
      setDeleting(null);
    }
  };

  const handleEdit = (contentId: number) => {
    window.location.href = `/blog/edit/${contentId}`;
  };

  const handleView = (contentId: number) => {
    window.open(`/blog/${contentId}`, '_blank');
  };

  const handleStatusChange = async (contentId: number, newStatus: string) => {
    try {
      setUpdatingStatus(contentId);
      await contentAPI.updateContent(contentId, { status: newStatus });

      // 로컬 상태 업데이트
      setContents(prevContents =>
        prevContents.map(content =>
          content.id === contentId
            ? { ...content, status: newStatus }
            : content
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : '상태 업데이트에 실패했습니다.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'published':
        return '발행됨';
      case 'draft':
        return '초안';
      case 'archived':
        return '보관됨';
      default:
        return status || '초안';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return '#10b981';
      case 'draft':
        return '#f59e0b';
      case 'archived':
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <h2>글 목록을 불러오는 중...</h2>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>블로그 관리</Title>
        <Subtitle>작성된 블로그 글을 관리하고 편집하세요</Subtitle>
      </Header>

      <MainContent>
        {error && (
          <ErrorMessage>
            <p>{error}</p>
            <button onClick={loadData}>다시 시도</button>
          </ErrorMessage>
        )}

        <FilterSection>
          <SearchInput>
            <input
              type="text"
              placeholder="제목이나 키워드로 검색..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </SearchInput>
          
          <FilterRow>
            <FilterGroup>
              <label>카테고리</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">전체</option>
                {categories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </FilterGroup>

            <FilterGroup>
              <label>상태</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">전체</option>
                <option value="published">발행됨</option>
                <option value="draft">초안</option>
                <option value="archived">보관됨</option>
              </select>
            </FilterGroup>

            <FilterGroup>
              <label>정렬 기준</label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <option value="created_at">작성일</option>
                <option value="updated_at">수정일</option>
                <option value="title">제목</option>
                <option value="seo_score">SEO 점수</option>
              </select>
            </FilterGroup>

            <FilterGroup>
              <label>정렬 순서</label>
              <select
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange('sortOrder', e.target.value as 'asc' | 'desc')}
              >
                <option value="desc">내림차순</option>
                <option value="asc">오름차순</option>
              </select>
            </FilterGroup>
          </FilterRow>
        </FilterSection>

        {filteredContents.length === 0 ? (
          <EmptyState>
            <h3>등록된 글이 없습니다</h3>
            <p>새로운 글을 작성해보세요!</p>
            <button onClick={() => window.location.href = '/blog/write'}>
              글 작성하기
            </button>
          </EmptyState>
        ) : (
          <ContentGrid>
            {filteredContents.map(content => (
              <ContentCard key={content.id}>
                <CardHeader>
                  <CardTitle>{content.title}</CardTitle>
                  <StatusSection>
                    <StatusBadge color={getStatusColor(content.status || 'draft')}>
                      {getStatusText(content.status || 'draft')}
                    </StatusBadge>
                    <StatusSelect
                      value={content.status || 'draft'}
                      onChange={(e) => handleStatusChange(content.id, e.target.value)}
                      disabled={updatingStatus === content.id}
                    >
                      <option value="draft">초안</option>
                      <option value="published">발행됨</option>
                      <option value="archived">보관됨</option>
                    </StatusSelect>
                  </StatusSection>
                </CardHeader>

                <CardMeta>
                  {content.category && (
                    <CategoryTag>{content.category.name}</CategoryTag>
                  )}
                  <MetaInfo>
                    작성일: {formatDate(content.created_at)}
                  </MetaInfo>
                  <MetaInfo>
                    작성자: {content.author_name || '관리자'}
                  </MetaInfo>
                  {content.seo_score > 0 && (
                    <MetaInfo>
                      SEO: {content.seo_score.toFixed(1)}점
                    </MetaInfo>
                  )}
                </CardMeta>

                {content.images && content.images.length > 0 && (
                  <ThumbnailImage src={content.images[0].src} alt={content.images[0].alt || content.title} />
                )}

                <ContentPreview>
                  {stripHtml(content.content).substring(0, 120)}...
                </ContentPreview>

                {content.keywords && content.keywords.length > 0 && (
                  <KeywordSection>
                    {content.keywords.slice(0, 3).map((keyword, index) => (
                      <KeywordTag key={index}>{keyword}</KeywordTag>
                    ))}
                    {content.keywords.length > 3 && (
                      <span>+{content.keywords.length - 3}개</span>
                    )}
                  </KeywordSection>
                )}

                <CardActions>
                  <ActionButton onClick={() => handleView(content.id)}>
                    보기
                  </ActionButton>
                  <ActionButton onClick={() => handleEdit(content.id)}>
                    수정
                  </ActionButton>
                  <ActionButton 
                    variant="danger"
                    onClick={() => handleDelete(content.id)}
                    disabled={deleting === content.id}
                  >
                    {deleting === content.id ? '삭제 중...' : '삭제'}
                  </ActionButton>
                </CardActions>
              </ContentCard>
            ))}
          </ContentGrid>
        )}
      </MainContent>
    </Container>
  );
};

// Styled Components - BlogWrite 스타일을 참고한 깔끔한 디자인
const Container = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding: 2rem 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60vh;
  text-align: center;
  
  h2 {
    color: #64748b;
    font-weight: 400;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #64748b;
  margin: 0;
`;

const MainContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 2rem;
  text-align: center;
  
  p {
    color: #dc2626;
    margin: 0 0 1rem 0;
  }
  
  button {
    background: #dc2626;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    cursor: pointer;
    font-weight: 500;
    
    &:hover {
      background: #b91c1c;
    }
  }
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const SearchInput = styled.div`
  margin-bottom: 1rem;
  
  input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: #6366f1;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }
    
    &::placeholder {
      color: #9ca3af;
    }
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
  }
  
  select {
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    background: white;
    color: #374151;
    min-width: 120px;
    
    &:focus {
      outline: none;
      border-color: #6366f1;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  
  h3 {
    font-size: 1.5rem;
    color: #374151;
    margin: 0 0 0.5rem 0;
  }
  
  p {
    color: #6b7280;
    margin: 0 0 1.5rem 0;
  }
  
  button {
    background: #6366f1;
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    font-weight: 500;
    cursor: pointer;
    font-size: 1rem;
    
    &:hover {
      background: #5856f0;
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.4;
  flex: 1;
`;

const StatusSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
`;

const StatusBadge = styled.span<{ color: string }>`
  background: ${props => props.color};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
`;

const StatusSelect = styled.select`
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: white;
  color: #374151;
  font-size: 0.75rem;
  cursor: pointer;
  min-width: 80px;

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
  align-items: center;
`;

const CategoryTag = styled.span`
  background: #f3f4f6;
  color: #374151;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
`;

const MetaInfo = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
`;

const ContentPreview = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
`;

const KeywordSection = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  
  span {
    font-size: 0.75rem;
    color: #9ca3af;
  }
`;

const KeywordTag = styled.span`
  background: #ede9fe;
  color: #6d28d9;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button<{ variant?: 'danger' }>`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.variant === 'danger' ? '#dc2626' : '#d1d5db'};
  border-radius: 0.375rem;
  background: ${props => props.variant === 'danger' ? '#dc2626' : 'white'};
  color: ${props => props.variant === 'danger' ? 'white' : '#374151'};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    ${props => props.variant === 'danger' 
      ? 'background: #b91c1c;' 
      : 'background: #f9fafb; border-color: #9ca3af;'
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default BlogManage;